/* eslint-disable no-nested-ternary */
/* eslint-disable object-shorthand */
/* eslint-disable camelcase */
import { useFormik } from 'formik';
import {
    useAddUpdateAssessmentMutation,
    useAssessmentDeleteMutation,
    useAssessmentSignMutation,
    useGetActivityQuery,
    useGetAssessmentDetailQuery,
    useGetAssessmentTypeQuery,
    useGetDiagnosesListQuery,
    useGetPatientLocationQuery,
    useGetPsychologicalTherapistListQuery
} from 'store/services/pshychologicalAssessment';
import { useGetPatientListQuery } from 'store/services/patient';
import useAuth from 'hooks/useAuth';
import { SERVICE_PSYCHOLOGICAL_TESTING } from 'constant';
import { initialValues, validationSchema } from './FormikConfig';
import useSnackbar from 'hooks/useSnackbar';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { encriptId } from 'utils/encription';
import usePermission from 'hooks/usePermission';
import { debounce } from 'lodash';

export const getPatientDiagnosisFromServiceInfo = (patient, serviceId) => {
    if (!patient || !serviceId) return null;

    const serviceInfo = patient?.service_info?.find((si) => String(si?.service) === String(serviceId));

    const primaryDiagnosis = serviceInfo?.primary_diagnosis;
    if (!primaryDiagnosis?.id) return null;

    return {
        id: primaryDiagnosis.id,
        icd_code: primaryDiagnosis.icd_code ?? primaryDiagnosis.code ?? '',
        description: primaryDiagnosis.description ?? primaryDiagnosis.name ?? ''
    };
};

export const useAssessmentForm = ({ AssessmentId, screenType = '' }) => {
    const { user } = useAuth();
    const { notify, notifyError } = useSnackbar();
    const navigate = useNavigate();
    const [save, { isLoading: isSaving }] = useAddUpdateAssessmentMutation();
    const { isPsychologist, isTherapist } = usePermission();

    function generatePayload(data) {
        try {
            // Format helper function for YYYY-MM-DD HH:mm (UTC)
            const formatDateTime = (dateValue, timeValue) => {
                if (!dateValue || !timeValue) return null;

                const date = new Date(dateValue);
                const time = new Date(timeValue);

                const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(
                    2,
                    '0'
                )}`;
                const hours = String(time.getUTCHours()).padStart(2, '0');
                const minutes = String(time.getUTCMinutes()).padStart(2, '0');

                return `${dateStr} ${hours}:${minutes}`;
            };
            return {
                id: AssessmentId,
                client_id: user?.client_id && user.client_id,
                patient_id: data?.student?.id || null,
                employee_id: data?.employee?.emp_id || null,
                service_id: data?.location?.service || null,
                location_id: data?.location?.location?.id || null,
                assessment_type_id: data?.assessmentType?.id || null,
                activity_type_id: data?.activity?.id || null,

                // date & time in YYYY-MM-DD HH:mm format (UTC)
                date: formatDateTime(data.session?.date, data.session?.startTime),
                start_time: formatDateTime(data.session?.date, data.session?.startTime),
                end_time: formatDateTime(data.session?.date, data.session?.endTime),

                notes: data?.notes || '',
                is_qualified: data?.qualified === 'yes',

                // diagnosis id converted into an array
                diagnosis_codes: data?.diagnosis?.id ? [data.diagnosis.id] : []
            };
        } catch (err) {
            console.log('Error in generatePayload:', err);
            return {};
        }
    }

    function generatePayloadv2(data) {
        try {
            // Format helper function for YYYY-MM-DD HH:mm (UTC)
            const formatDateTime = (dateValue, timeValue) => {
                if (!dateValue || !timeValue) return null;

                // Handle Day.js objects
                const getDateString = (date) => {
                    if (date.$isDayjsObject) {
                        return date.format('YYYY-MM-DD');
                    }
                    if (date instanceof Date) {
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        return `${year}-${month}-${day}`;
                    }
                    return String(date).split('T')[0];
                };

                const getTimeString = (time) => {
                    const timeObj = new Date(time);
                    const hours = String(timeObj.getHours()).padStart(2, '0');
                    const minutes = String(timeObj.getMinutes()).padStart(2, '0');
                    return `${hours}:${minutes}`;
                };

                // Combine date and time in local timezone
                const dateStr = getDateString(dateValue);
                const timeStr = getTimeString(timeValue);

                // Create combined datetime and convert to UTC
                const combined = new Date(`${dateStr}T${timeStr}:00`);

                // Extract UTC date and time
                const utcYear = combined.getUTCFullYear();
                const utcMonth = String(combined.getUTCMonth() + 1).padStart(2, '0');
                const utcDay = String(combined.getUTCDate()).padStart(2, '0');
                const utcHours = String(combined.getUTCHours()).padStart(2, '0');
                const utcMinutes = String(combined.getUTCMinutes()).padStart(2, '0');

                return `${utcYear}-${utcMonth}-${utcDay} ${utcHours}:${utcMinutes}`;
            };

            return {
                id: AssessmentId,
                client_id: user?.client_id && user.client_id,
                patient_id: data?.student?.id || null,
                employee_id: data?.employee?.emp_id || null,
                service_id: data?.location?.service || null,
                location_id: data?.location?.location?.id || null,
                assessment_type_id: data?.assessmentType?.id || null,
                activity_type_id: data?.activity?.id || null,

                // date & time in YYYY-MM-DD HH:mm format (UTC)
                date: formatDateTime(data.session?.date, data.session?.startTime),
                start_time: formatDateTime(data.session?.date, data.session?.startTime),
                end_time: formatDateTime(data.session?.date, data.session?.endTime),

                notes: data?.notes || '',
                is_qualified: data?.qualified === 'yes',

                // diagnosis id converted into an array
                diagnosis_codes: data?.diagnosis?.id ? [data.diagnosis.id] : []
            };
        } catch (err) {
            console.log('Error in generatePayload:', err);
            return {};
        }
    }
    // ✅ Separate onSubmit handler
    const handleFormSubmit = async (values, source = null) => {
        try {
            const payload = generatePayloadv2(values);
            const resp = await save(payload).unwrap();
            if (source === 'Sign') {
                return;
            }
            notify({
                message: resp.message,
                severity: 'success'
            });

            if (!AssessmentId) {
                navigate(`/psychologicaltesting/edit/${encriptId(resp?.fields?.id)}`);
            }
        } catch (error) {
            notifyError({ error });
        }
    };

    // ✅ Formik setup
    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: handleFormSubmit
    });

    const [deleteAssessment, { isLoading: isDeleting }] = useAssessmentDeleteMutation();

    // In your component:
    const [searchTerm, setSearchTerm] = useState('');

    // Debounced search handler
    const handleSearchChange = useCallback(
        debounce((value) => {
            setSearchTerm(value);
        }, 500), // 500ms delay
        []
    );

    const {
        data: studentData,
        isLoading: studentIsLoading,
        isFetching: studentIsFetching
    } = useGetPatientListQuery(
        {
            filters: {
                ...(user?.client_id && { client_id: user.client_id }),
                order_col: 'name',
                order: 'asc',
                services: [SERVICE_PSYCHOLOGICAL_TESTING],
                name: searchTerm
            }
        },
        {
            skip: !user?.client_id || searchTerm.length < 3 || screenType === 'view'
        }
    );

    const {
        data: locations,
        isFetching: locationFetching,
        isLoading: locationLoading
    } = useGetPatientLocationQuery(
        {
            ...(user?.client_id && { client_id: user.client_id }),
            patient_id: formik?.values?.student?.id,
            service_id: SERVICE_PSYCHOLOGICAL_TESTING
        },
        { skip: !formik?.values?.student?.id || screenType === 'view' }
    );

    const {
        data: therapistList,
        isFetching: therapyFetching,
        isLoading: therapyLoading
    } = useGetPsychologicalTherapistListQuery(
        {
            filters: {
                ...(user?.client_id && { clients: [user.client_id] }),
                locations: [formik?.values?.location?.location?.id],
                services: [SERVICE_PSYCHOLOGICAL_TESTING],
                source: 'psychological-testing'
                // order_col: 'name',
                // order: 'asc'
            }
        },
        {
            skip:
                screenType === 'view'
                    ? true
                    : isPsychologist || isTherapist
                      ? false // If psychologist or therapist → do NOT skip
                      : !formik?.values?.location?.id // Others → skip if no location
        }
    );

    const {
        data: diagnoses,
        isFetching: diagnosesFetching,
        isLoading: diagnosesLoading
    } = useGetDiagnosesListQuery(
        {
            filters: {
                service_id: SERVICE_PSYCHOLOGICAL_TESTING
            }
        },
        { skip: screenType === 'view' }
    );

    const handleStudentChange = (patient) => {
        formik.setFieldValue('student', patient);
        formik.setFieldValue('location', null);
        if (!AssessmentId) {
            const patientDiagnosis = getPatientDiagnosisFromServiceInfo(patient, SERVICE_PSYCHOLOGICAL_TESTING);
            const diagnosis = diagnoses?.fields?.find((item) => String(item.id) === String(patientDiagnosis?.id)) || null;
            formik.setFieldValue('diagnosis', diagnosis);
        }
        if (!isPsychologist && !isTherapist) {
            formik.setFieldValue('employee', null);
        }
    };

    const {
        data: assessmentTypes,
        isFetching: assessmentFetching,
        isLoading: assessmentLoading
    } = useGetAssessmentTypeQuery({}, { skip: screenType === 'view' });
    const {
        data: activities,
        isFetching: activityFetching,
        isLoading: activityLoading
    } = useGetActivityQuery({}, { skip: screenType === 'view' });
    const {
        data: detail,
        isFetching: detailFetching,
        isLoading: detailLoading
    } = useGetAssessmentDetailQuery(
        {
            id: AssessmentId,
            client_id: user?.client_id
        },
        {
            skip: !AssessmentId
        }
    );

    useEffect(() => {
        if (!AssessmentId || !detail?.fields) return;

        try {
            const fields = detail.fields;

            // Parse date and times from the API response
            // const sessionDate = fields.date ? `${fields.date}`.split('T')[0] : '';
            const sessionDate = fields.date
                ? new Date(fields.date).toLocaleDateString('en-CA') // en-CA = YYYY-MM-DD
                : '';

            // Create Date objects for start and end times
            // Assuming the backend stores full datetime in UTC
            const startDateTime = new Date(`${fields.start_time}`);
            const endDateTime = new Date(`${fields.end_time}`);

            // You'll need to extract actual start/end times from your backend
            // If they're stored separately in fields, adjust accordingly
            // For now, using placeholder times - replace with actual fields

            formik.setValues({
                student: fields.patient
                    ? {
                          id: fields.patient.id,
                          user: {
                              name: fields.patient.name
                          }
                      }
                    : null,

                location: fields.location
                    ? {
                          id: fields.location.id,
                          location: {
                              id: fields.location.id,
                              name: fields.location.name
                          },
                          service: fields.service?.id
                      }
                    : null,

                employee: fields.employee
                    ? {
                          emp_id: fields.employee.id,
                          name: fields.employee.name,
                          credentials: fields.employee.credentials
                      }
                    : null,

                diagnosis:
                    fields.diagnosis && fields.diagnosis.length > 0
                        ? {
                              id: fields.diagnosis[0]?.id, // Taking first diagnosis ID
                              icd_code: fields.diagnosis[0]?.code, // Taking first diagnosis ID
                              description: fields.diagnosis[0]?.name
                          }
                        : null,

                session: {
                    date: sessionDate,
                    startTime: startDateTime, // Adjust based on your TimePicker format
                    endTime: endDateTime // Adjust based on your TimePicker format
                },

                assessmentType: fields.assessment_type
                    ? {
                          id: fields.assessment_type?.id,
                          name: fields.assessment_type?.name
                          // You may need to find the full object from assessmentTypes list
                      }
                    : null,

                qualified: fields.is_qualified ? 'yes' : 'no',

                activity: fields.activity_type
                    ? {
                          id: fields.activity_type?.id,
                          name: fields.activity_type?.name
                      }
                    : null,

                notes: fields.notes || ''
            });
        } catch (err) {
            console.error('Failed to populate assessment detail', err);
        }
    }, [detail, AssessmentId]);

    // Helper effect to populate full objects once dropdown data is loaded
    useEffect(() => {
        if (!detail?.fields || !formik.values.diagnosis) return;

        // Populate full diagnosis object
        if (diagnoses?.fields && formik.values.diagnosis?.id && !formik.values.diagnosis.icd_code) {
            const fullDiagnosis = diagnoses.fields.find((d) => d.id === formik.values.diagnosis.id);
            if (fullDiagnosis) {
                formik.setFieldValue('diagnosis', fullDiagnosis);
            }
        }

        // Populate full assessment type object
        if (assessmentTypes?.fields && formik.values.assessmentType?.id && !formik.values.assessmentType.name) {
            const fullAssessmentType = assessmentTypes.fields.find((a) => a.id === formik.values.assessmentType.id);
            if (fullAssessmentType) {
                formik.setFieldValue('assessmentType', fullAssessmentType);
            }
        }

        // Populate full activity object
        if (activities?.fields && formik.values.activity?.id && !formik.values.activity.name) {
            const fullActivity = activities.fields.find((a) => a.id === formik.values.activity.id);
            if (fullActivity) {
                formik.setFieldValue('activity', fullActivity);
            }
        }
    }, [detail, diagnoses, assessmentTypes, activities]);

    const [sign, { isLoading: isSigning }] = useAssessmentSignMutation();

    const handleEsign = async (password, id) => {
        try {
            await handleFormSubmit(formik?.values, 'Sign');
            const payload = {
                id,
                password
            };
            const res = await sign(payload).unwrap();
            notify({
                message: res.message,
                severity: 'success'
            });
        } catch (error) {
            console.log('error', error);
            notifyError({
                error
            });
        }
    };

    useEffect(() => {
        // Redirect immediately if user tries to go back
        const handleBeforeUnload = (e) => {
            navigate('/psychologicaltesting', { replace: true });
        };

        window.history.pushState(null, '', window.location.href); // Add dummy state
        window.addEventListener('popstate', handleBeforeUnload);

        return () => {
            window.removeEventListener('popstate', handleBeforeUnload);
        };
    }, [navigate]);

    return {
        formik,
        studentData,
        studentIsLoading,
        studentIsFetching,
        locations,
        locationFetching,
        locationLoading,
        therapistList,
        therapyFetching,
        therapyLoading,
        diagnoses,
        assessmentTypes,
        activities,

        assessmentFetching,
        assessmentLoading,
        activityFetching,
        activityLoading,
        diagnosesFetching,
        diagnosesLoading,
        isSaving,

        detailFetching,
        detailLoading,
        detail,
        handleEsign,
        isSigning,
        deleteAssessment,
        isDeleting,
        user,
        isPsychologist,
        isTherapist,
        handleSearchChange,
        handleStudentChange
    };
};
